import {
  Text,
  Select,
  Flex,
  Spinner,
  IconButton,
  NumberInput,
  NumberInputField,
  Switch,
  Tooltip,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  useGetLocationElectricityCostQuery,
  useUpdateLocationElectricityCostMutation,
} from "../api/dasboardApi";
import { ElectricityCostType } from "../types";
import { EditIcon, CheckIcon } from "@chakra-ui/icons";
import { toast } from "react-toastify";

export function ElectricityCost() {
  const { data: locations, isLoading, refetch } = useGetLocationElectricityCostQuery();
  const [editCost] = useUpdateLocationElectricityCostMutation();

  const [selectedLocation, setSelectedLocation] = useState<ElectricityCostType | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPrice, setEditedPrice] = useState("");
  const [prevPrice, setPrevPrice] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showIbex, setShowIbex] = useState(false);

  const getSafePrice = (price: string | number | null | undefined) => {
    return price !== null && price !== undefined && !isNaN(Number(price))
      ? Number(price).toFixed(2)
      : "0.00";
  };

  useEffect(() => {
    if (!selectedLocation && locations?.length) {
      setSelectedLocation(locations[0]);
    } else if (locations && selectedLocation) {
      const updated = locations.find((loc) => loc.id === selectedLocation.id);
      if (updated) {
        setSelectedLocation(updated);
        if (!isEditing) setEditedPrice(getSafePrice(updated.price));
      }
    }
  }, [locations, selectedLocation, isEditing]);

  const handleEdit = () => {
    if (!selectedLocation) return;

    setPrevPrice(selectedLocation.price ?? "0");
    setEditedPrice(getSafePrice(selectedLocation.price));
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!selectedLocation) return;

    if (editedPrice.trim() === "" || isNaN(Number(editedPrice))) {
      toast.error("Please enter a valid price.");
      return;
    }

    setIsSaving(true);
    setIsEditing(false);

    try {
      const updated = await editCost({
        id: selectedLocation.id,
        body: {
          ...selectedLocation,
          price: editedPrice,
          price_currency: selectedLocation.price_currency.toLowerCase(),
          price_unit: selectedLocation.price_unit.toUpperCase(),
        },
      }).unwrap();

      setSelectedLocation(updated[0]);
      await refetch();

      toast.success("Electricity cost updated successfully.", {
        style: { padding: "10px 20px" },
      });
    } catch (error) {
      setEditedPrice(prevPrice);
      setSelectedLocation((prev) => (prev ? { ...prev, price: prevPrice } : prev));
      toast.error("Failed to update electricity cost.", {
        style: { padding: "10px 20px" },
      });
      setIsEditing(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLoc = locations?.find((loc) => loc.title === e.target.value);
    if (newLoc) {
      setSelectedLocation(newLoc);
      setIsEditing(false);
      setEditedPrice(getSafePrice(newLoc.price));
    }
  };

  return (
    <Flex
      width={{ base: '100%', desktopLg: '500px' }}
      height="max-content"
      direction="column"
      borderRadius="xl"
      p={4}
      bg="white"
      boxShadow="sm"
    >
      {isLoading ? (
        <Flex justify="center" align="center" height="150px">
          <Spinner size="lg" />
        </Flex>
      ) : (
        <>
          <Flex justify="space-between" align="center" mb={2}>
            <Flex align="center" gap={2}>
              <Text fontWeight={700} fontSize="18px" color="#484848">
                Electricity cost
              </Text>
              {selectedLocation?.price_currency && (
                <IconButton
                  icon={isEditing ? <CheckIcon /> : <EditIcon />}
                  aria-label={isEditing ? 'Save' : 'Edit'}
                  size="sm"
                  variant="ghost"
                  onClick={isEditing ? handleSave : handleEdit}
                  isLoading={isSaving}
                  isDisabled={showIbex}
                />
              )}
              <Tooltip
                hasArrow
                placement="top"
                bg="heading.dark"
                label={showIbex ? "Show self-input price" : "Show IBEX"}
                openDelay={300}
                closeDelay={200}
              >
                <Flex align="center" justify="center">
                  <Switch
                    size="sm"
                    colorScheme="blue"
                    isChecked={showIbex}
                    onChange={(e) => setShowIbex(e.target.checked)}
                  />
                </Flex>
              </Tooltip>
            </Flex>
            <Select
              value={selectedLocation?.title ?? ''}
              onChange={handleLocationChange}
              size="sm"
              maxW="160px"
              fontSize="sm"
              variant="outline"
              isDisabled={isEditing || isSaving}
            >
              {locations?.map((location) => (
                <option key={location.id} value={location.title}>
                  {location.title}
                </option>
              ))}
            </Select>
          </Flex>

          <Flex align="center" gap={2} minH="40px">
            {isEditing && !showIbex ? (
              <NumberInput
                size="sm"
                maxW="120px"
                value={editedPrice}
                onChange={(value) => setEditedPrice(value)}
                precision={2}
                step={0.01}
                clampValueOnBlur
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (editedPrice.trim() === "" || isNaN(Number(editedPrice))) {
                      toast.error("Please enter a valid price.");
                      return;
                    }
                    handleSave();
                  } else if (e.key === "Escape") {
                    setIsEditing(false);
                    setEditedPrice(prevPrice);
                  }
                }}
              >
                <NumberInputField autoFocus />
              </NumberInput>
            ) : (
              <Text fontWeight={700} fontSize="24px" color="#484848">
                {(() => {
                  const value = showIbex
                    ? selectedLocation?.ibex_price
                    : isSaving
                      ? editedPrice
                      : selectedLocation?.price;

                  return value !== null && value !== undefined && !isNaN(Number(value))
                    ? Number(value).toFixed(2)
                    : "0.00";
                })()}{" "}
                {selectedLocation?.price_currency?.toUpperCase()}
                <Text
                  as="span"
                  ml={2}
                  fontSize="20px"
                  fontWeight={700}
                  color="#9B9B9B"
                >
                  {selectedLocation?.price_unit}
                </Text>
              </Text>
            )}
          </Flex>
        </>
      )}
    </Flex>
  )
}
